import React from 'react';
import { Alert, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import { OrderQuotaImportResult } from '../graphql/orderQuotaGraphql';
import { useSkippedText } from '../hooks/useSheetImport';
import LineList from './LineList';

interface ImportPreviewProps {
    preview: OrderQuotaImportResult;
    /** 预览汇总文案；导入后改为提示跳过的错误行 */
    summary: string;
    /** 错误明细中对象编码列的表头 */
    codeHeader: string;
}

/**
 * 导入预览：汇总提示与错误行明细（行号、对象编码、原因）。
 */
const ImportPreview: React.FC<ImportPreviewProps> = ({ preview, summary, codeHeader }) => {
    const { t } = useTranslation();
    const skippedText = useSkippedText();

    return (
        <>
            <Alert
                type={preview.errors.length > 0 ? 'warning' : 'success'}
                showIcon
                message={preview.applied ? skippedText(preview) : summary}
            />
            {preview.errors.length > 0 && (
                <Table
                    size="small"
                    rowKey="lineNo"
                    pagination={{ pageSize: 10 }}
                    dataSource={preview.errors}
                    columns={[
                        { title: t('central.orderQuota.import.lineNo'), dataIndex: 'lineNo', width: 80 },
                        { title: codeHeader, dataIndex: 'targetCode', width: 160 },
                        {
                            title: t('central.orderQuota.import.reason'),
                            dataIndex: 'messages',
                            render: (messages: string[]) => <LineList lines={messages} />,
                        },
                    ]}
                />
            )}
        </>
    );
};

/**
 * 导入被后端整体拒绝的原因：首行为标题，其余每个错误一行（如容量校验不通过的各 HUB、各日期）；内容过高时滚动。
 */
export const ImportRejection: React.FC<{ reason: string }> = ({ reason }) => {
    const [title, ...details] = reason.split('\n');
    return (
        <Alert
            type="error"
            showIcon
            message={title}
            description={
                details.length > 0 ? (
                    <div className="max-h-72 overflow-y-auto">
                        <LineList lines={details} />
                    </div>
                ) : undefined
            }
        />
    );
};

export default ImportPreview;
